import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Zap, 
  Droplets, 
  Users,
  AlertTriangle,
  CheckCircle,
  Home,
  LogOut,
  Download,
  Trash2,
  Shield,
  ShieldCheck,
  BarChart3,
  FileText,
  Mail,
  Phone,
  Calendar,
  RefreshCw,
  ChevronLeft,
  Trophy,
  Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [outages, setOutages] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, outagesRes, quotesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/outages`),
        axios.get(`${API}/admin/quotes`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setOutages(outagesRes.data.outages);
      setQuotes(quotesRes.data.quotes);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Accès admin requis');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    if (!user?.is_admin) {
      toast.error('Accès admin requis');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAuthenticated, user, navigate, fetchData]);

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { is_admin: !currentStatus });
      toast.success('Statut admin mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteOutage = async (outageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette panne ?')) return;
    
    try {
      await axios.delete(`${API}/outages/${outageId}`);
      toast.success('Panne supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResolveOutage = async (outageId) => {
    try {
      await axios.patch(`${API}/outages/${outageId}/resolve`);
      toast.success('Panne marquée comme résolue');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleMarkContacted = async (quoteId) => {
    try {
      await axios.patch(`${API}/solar/contacted/${quoteId}`);
      toast.success('Marqué comme contacté');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleExport = async (dataType) => {
    try {
      window.open(`${API}/admin/export?format=csv&data_type=${dataType}`, '_blank');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d MMM yyyy 'à' HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <ChevronLeft className="h-5 w-5" />
                <span className="hidden sm:inline">Retour</span>
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span className="font-bold text-xl tracking-tight">Admin Dashboard</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={logout}
              >
                <LogOut className="h-4 w-4" />
              </Button>

              <Link to="/">
                <Button variant="ghost" size="sm">
                  <Home className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="outages" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Pannes
            </TabsTrigger>
            <TabsTrigger value="quotes" className="gap-2">
              <FileText className="h-4 w-4" />
              Devis
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Utilisateurs</p>
                      <p className="text-3xl font-bold mt-1">{stats?.total_users || 0}</p>
                    </div>
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Pannes actives</p>
                      <p className="text-3xl font-bold text-destructive mt-1">{stats?.active_outages || 0}</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-destructive" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Résolues</p>
                      <p className="text-3xl font-bold text-secondary mt-1">{stats?.resolved_outages || 0}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-secondary" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Devis en attente</p>
                      <p className="text-3xl font-bold text-primary mt-1">{stats?.pending_quotes || 0}</p>
                    </div>
                    <FileText className="h-8 w-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-primary" />
                    Pannes par type
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary" />
                      Électricité (SNEL)
                    </span>
                    <span className="font-bold">{stats?.electricity_outages || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Droplets className="h-4 w-4 text-blue-500" />
                      Eau (REGIDESO)
                    </span>
                    <span className="font-bold">{stats?.water_outages || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Exporter les données
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={() => handleExport('outages')}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    Exporter les pannes (CSV)
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={() => handleExport('users')}
                  >
                    <Users className="h-4 w-4" />
                    Exporter les utilisateurs (CSV)
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={() => handleExport('quotes')}
                  >
                    <FileText className="h-4 w-4" />
                    Exporter les devis (CSV)
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Utilisateurs ({users.length})</CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('users')}>
                  <Download className="h-4 w-4 mr-2" />
                  Exporter
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="divide-y divide-border">
                    {users.map((u) => (
                      <div key={u.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.is_admin ? 'bg-primary/20' : 'bg-muted'}`}>
                            {u.is_admin ? (
                              <ShieldCheck className="h-5 w-5 text-primary" />
                            ) : (
                              <Users className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{u.name}</p>
                              {u.is_admin && (
                                <Badge className="bg-primary/20 text-primary">Admin</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Trophy className="h-3 w-3" />
                                {u.points || 0} points
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(u.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Admin</span>
                            <Switch
                              checked={u.is_admin}
                              onCheckedChange={() => handleToggleAdmin(u.id, u.is_admin)}
                              disabled={u.id === user.id}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(u.id)}
                            disabled={u.id === user.id}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Outages Tab */}
          <TabsContent value="outages">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Pannes ({outages.length})</CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('outages')}>
                  <Download className="h-4 w-4 mr-2" />
                  Exporter
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="divide-y divide-border">
                    {outages.map((o) => (
                      <div key={o.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${o.type === 'electricity' ? 'bg-primary/10' : 'bg-blue-500/10'}`}>
                              {o.type === 'electricity' ? (
                                <Zap className="h-5 w-5 text-primary" />
                              ) : (
                                <Droplets className="h-5 w-5 text-blue-500" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">
                                  {o.type === 'electricity' ? 'Électricité' : 'Eau'}
                                </span>
                                <Badge className={o.status === 'active' ? 'bg-destructive/20 text-destructive' : 'bg-secondary/20 text-secondary'}>
                                  {o.status === 'active' ? 'Active' : 'Résolue'}
                                </Badge>
                                <Badge variant="outline">{o.severity}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{o.location_name}</p>
                              <p className="text-sm mt-1">{o.description}</p>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                                <span>Par: {o.reporter_name}</span>
                                <span>{o.confirmations || 0} confirmations</span>
                                <span>{formatDate(o.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {o.status === 'active' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResolveOutage(o.id)}
                                className="text-secondary"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Résoudre
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteOutage(o.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Demandes de devis ({quotes.length})</CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('quotes')}>
                  <Download className="h-4 w-4 mr-2" />
                  Exporter
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {quotes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                      <FileText className="h-12 w-12 mb-4" />
                      <p>Aucune demande de devis</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {quotes.map((q) => (
                        <div key={q.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{q.name}</span>
                                <Badge className={q.contacted ? 'bg-secondary/20 text-secondary' : 'bg-primary/20 text-primary'}>
                                  {q.contacted ? 'Contacté' : 'En attente'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {q.email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {q.phone}
                                </span>
                              </div>
                              {q.message && (
                                <p className="text-sm mt-2">{q.message}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatDate(q.created_at)}
                              </p>
                            </div>
                            {!q.contacted && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkContacted(q.id)}
                                className="text-secondary"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Marquer contacté
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
